import { useContext, useEffect, useRef, useState } from 'react';
import {
  FooterButtons,
  ButtonActions,
  FeedbackContainer,
  FeedbackHeader,
  FeedbackTabContainer,
  FeedbackWidgetContainer,
  MobileOpenFormButton,
  FeedbackBackdrop,
  FeedbackTabViewTitle,
} from './styles/Feedback.styled';
import { Button } from '@fluentui/react-components';
import { useGetUserFeedbacks } from './hooks/useGetUserFeedbacks';
import { LoadingSpinner } from 'src/components/common/LoadingSpinner/LoadingSpinner';
import { useGetFeedbackTopics } from './hooks/useGetFeedbackTopics';
import { useGetCompanyValues } from './hooks/useGetCompanyValues';
import Agile from '../assets/companyvaluesicons/agile.svg';
import Collaboration from '../assets/companyvaluesicons/collaboration.svg';
import Growth from '../assets/companyvaluesicons/Growth.svg';
import Highfive from '../assets/companyvaluesicons/hightfive.svg';
import TwoCheckmarks from '../assets/companyvaluesicons/proficon.svg';
import { useGetAllUsers } from './hooks/useGetAllUsers';
import { TeamsFxContext } from 'src/context/TeamsContext';
import { FeedbackForm } from 'src/components/Feedback/FeedbackForm/FeedbackForm';
import { FeedbackTable } from 'src/components/Feedback/FeedbackTable/FeedbackTable';
import { isMobile } from 'react-device-detect';
import {
  Add20Regular,
  Add20Filled as AddIcon,
  Dismiss20Regular,
} from '@fluentui/react-icons';
import { useSendFeedback } from './hooks/useSendFeedback';
import { Alert } from 'src/components/common/AlertMessage/AlertMessage';
import { useAlertMessage } from 'src/hooks/useAlertMessage';
import { FEEDBACK_SUBMIT_SUCCESS_MESSAGE } from './constants/Feedback.constants';
import { PrimaryButton } from 'src/styles/globalStyles/buttonStyles.styled';

const iconsArray = [Agile, Growth, Collaboration, TwoCheckmarks, Highfive];

const formError = 'Please fill in all the required fields.';

const selectedEmployeeInitialValues = {
  requestedBy: '',
  employee: '',
};

const initialErrors = {
  topic: '',
  employee: '',
  feedback: '',
  companyValues: '',
};

export const Feedback = () => {
  const fieldRef = useRef(null);
  const { loadingSendFeedback, updateRequestedFeedback, sendFeedback } =
    useSendFeedback();
  const { currentUserEmail } = useContext(TeamsFxContext);
  const { users, loadingUsers } = useGetAllUsers();
  const { feedbacks, loadingFeedbacks, getUserFeedbacks } =
    useGetUserFeedbacks();
  const { feedbackTopics, loadingFeedbackTopics } = useGetFeedbackTopics();
  const { companyValues, loadingCompanyValues } = useGetCompanyValues();
  const [comboboxWidth, setComboboxWidth] = useState(0);
  const [selectedTopic, setSelectedTopic] = useState('');
  const [selectedEmployeeValues, setSelectedEmployeeValues] = useState(
    selectedEmployeeInitialValues
  );
  const [selectedFeedbackId, setSelectedFeedbackId] = useState('');
  const [feedbackTextArea, setFeedbackTextArea] = useState('');
  const [selectedCompanyValues, setSelectedCompanyValues] = useState([]);
  const [openSubmitForm, setOpenSubmitForm] = useState(false);
  const {
    hideAlert,
    requestStatusAppearance,
    requestStatusMessage,
    showAlert,
  } = useAlertMessage();
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [mobileFormOpen, setMobileFormOpen] = useState(false);
  const [formErrors, setFormErrors] = useState(initialErrors);
  const { employee, requestedBy } = selectedEmployeeValues;
  const feedbackEmptyState = !selectedFeedbackId;
  const isFormValid =
    formErrors.topic || formErrors.employee || formErrors.feedback;
  const filteredUsers = employee
    ? users?.filter(
        (user) =>
          user.new_name.toLowerCase().includes(employee.toLowerCase()) &&
          user.cra0e_email !== currentUserEmail
      )
    : [];
  const currentUser = users?.find(
    (user) => user.cra0e_email === currentUserEmail
  );
  useEffect(() => {
    if (fieldRef.current) {
      setComboboxWidth(fieldRef.current.offsetWidth);
    }
  }, []);

  if (
    loadingFeedbacks ||
    loadingFeedbackTopics ||
    loadingCompanyValues ||
    loadingUsers
  ) {
    return <LoadingSpinner />;
  }

  const companyValuesIconsMapped = companyValues?.map((value, index) => ({
    ...value,
    icon: iconsArray[index],
  }));

  const clearForm = () => {
    setSelectedEmployeeValues(selectedEmployeeInitialValues);
    setFeedbackTextArea('');
    setSelectedTopic('');
    setSelectedFeedbackId('');
    setSelectedCompanyValues([]);
    setSelectedEmployeeId('');
    setFormErrors(initialErrors);
  };

  const closeAndClearForm = () => {
    clearForm();
    setOpenSubmitForm(false);
    setMobileFormOpen(false);
  };

  const mobileOpenForm = () => {
    setMobileFormOpen(true);
    setOpenSubmitForm(true);
  };

  const submitFeedback = async () => {
    if (!selectedTopic || !selectedEmployeeId || !feedbackTextArea.trim()) {
      if (!selectedEmployeeId) {
        setFormErrors((prev) => ({
          ...prev,
          employee: formError,
        }));
      }
      if (!feedbackTextArea.trim()) {
        setFormErrors((prev) => ({
          ...prev,
          feedback: formError,
        }));
      }

      if (!selectedTopic) {
        setFormErrors((prev) => ({
          ...prev,
          topic: formError,
        }));
      }

      return;
    }

    if (selectedFeedbackId) {
      await updateRequestedFeedback({
        cra0e_employeefeedbackid: selectedFeedbackId,
        cra0e_companyvalues: {
          value: selectedCompanyValues.join(','),
        },
        cra0e_details: feedbackTextArea,
      });
      showAlert(FEEDBACK_SUBMIT_SUCCESS_MESSAGE, 'success');
      clearForm();
      setOpenSubmitForm(false);
      setMobileFormOpen(false);
      getUserFeedbacks();
    } else {
      await sendFeedback({
        cra0e_companyvalues: {
          value: selectedCompanyValues.join(','),
        },
        cra0e_details: feedbackTextArea,
        cra0e_employee: { new_employeeid: selectedEmployeeId },
        cra0e_topic: { cra0e_feedbacktopicid: selectedTopic },
      });
      showAlert(FEEDBACK_SUBMIT_SUCCESS_MESSAGE, 'success');
      clearForm();
      setOpenSubmitForm(false);
      setMobileFormOpen(false);
    }
  };

  const showClearAllButton =
    selectedFeedbackId ||
    selectedTopic ||
    selectedCompanyValues.length ||
    selectedEmployeeId ||
    selectedTopic ||
    feedbackTextArea.trim() ||
    employee;

  return (
    <FeedbackTabContainer>
      {requestStatusMessage && (
        <Alert
          requestStatusAppearance={requestStatusAppearance}
          requestStatusMessage={requestStatusMessage}
          showCloseButton={true}
          overlay={true}
          handleClose={hideAlert}
        />
      )}
      {!mobileFormOpen && isMobile && feedbacks.length !== 0 && (
        <MobileOpenFormButton onClick={mobileOpenForm}>
          <AddIcon />
          New feedback
        </MobileOpenFormButton>
      )}
      {!mobileFormOpen && (
        <FeedbackContainer>
          <FeedbackHeader>
            <FeedbackTabViewTitle>Feedback requests</FeedbackTabViewTitle>
            {!isMobile && feedbacks.length > 0 && (
              <PrimaryButton
                onClick={() => setOpenSubmitForm(true)}
                size="medium"
                icon={<Add20Regular />}
                iconPosition="before"
              >
                New Feedback
              </PrimaryButton>
            )}
          </FeedbackHeader>
          <FeedbackTable
            feedbacks={feedbacks}
            selectedFeedbackId={selectedFeedbackId}
            clearForm={clearForm}
            setSelectedEmployeeValues={setSelectedEmployeeValues}
            setSelectedTopic={setSelectedTopic}
            setSelectedFeedbackId={setSelectedFeedbackId}
            setMobileFormOpen={setMobileFormOpen}
            setSelectedEmployeeId={setSelectedEmployeeId}
            setOpenSubmitForm={setOpenSubmitForm}
          />
          {!isMobile && feedbacks.length !== 0 && <ButtonActions />}
        </FeedbackContainer>
      )}
      {openSubmitForm && (mobileFormOpen || !isMobile) && (
        <>
          <FeedbackBackdrop />
          <FeedbackWidgetContainer>
            <FeedbackHeader>
              <span>Submit feedback</span>
              <Dismiss20Regular onClick={closeAndClearForm} />
            </FeedbackHeader>
            <FeedbackForm
              selectedTopic={selectedTopic}
              comboboxWidth={comboboxWidth}
              selectedFeedbackId={selectedFeedbackId}
              employee={employee}
              companyValuesIconsMapped={companyValuesIconsMapped}
              selectedCompanyValues={selectedCompanyValues}
              feedbackTextArea={feedbackTextArea}
              filteredUsers={filteredUsers}
              selectedEmployeeId={selectedEmployeeId}
              setSelectedCompanyValues={setSelectedCompanyValues}
              setFormErrors={setFormErrors}
              setFeedbackTextArea={setFeedbackTextArea}
              setSelectedEmployeeValues={setSelectedEmployeeValues}
              setSelectedEmployeeId={setSelectedEmployeeId}
              setSelectedTopic={setSelectedTopic}
              formErrors={formErrors}
              requestedBy={requestedBy}
              feedbackEmptyState={feedbackEmptyState}
              fieldRef={fieldRef}
              isFormValid={isFormValid}
              feedbackTopics={feedbackTopics}
              currentUser={currentUser}
            />
            <FooterButtons>
              <p onClick={clearForm}>{showClearAllButton ? 'Clear all' : ''}</p>
              <ButtonActions>
                <Button appearance="secondary" onClick={closeAndClearForm}>
                  Discard
                </Button>
                <Button
                  appearance="primary"
                  onClick={submitFeedback}
                  disabled={loadingSendFeedback}
                >
                  Submit
                </Button>
              </ButtonActions>
            </FooterButtons>
          </FeedbackWidgetContainer>
        </>
      )}
    </FeedbackTabContainer>
  );
};
